/**
 * Wizard answer persistence — DC-WA-FLOW-JL26
 *
 * The qualification wizard NEVER submits a lead. GoHighLevel remains the one
 * and only lead source, so there is zero risk of duplicate leads or inflated
 * conversion counts. This module only keeps the visitor's answers in the
 * browser so that:
 *   - closing the tab mid-flow does not reset progress
 *   - the answers can be shown back as a summary above the GHL form
 *   - the answers can be attached to Meta events for funnel diagnostics
 */

export type WizardAnswers = {
  procedure: string | null
  timeline: string | null
  credit: string | null
  medical: string[]
  height: string
  weight: string
}

export const EMPTY_ANSWERS: WizardAnswers = {
  procedure: null,
  timeline: null,
  credit: null,
  medical: [],
  height: '',
  weight: '',
}

const STORAGE_KEY = 'dc_wizard_answers_jl26'
const STEP_KEY = 'dc_wizard_step_jl26'
const DONE_KEY = 'dc_wizard_done_jl26'

/**
 * A patient who already answered the questions must never be asked again.
 * On return they land straight on their summary + the contact form.
 */
export function loadCompleted(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(DONE_KEY) === '1'
  } catch {
    return false
  }
}

export function saveCompleted() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(DONE_KEY, '1')
  } catch {
    // Non-critical
  }
}

export function loadAnswers(): WizardAnswers {
  if (typeof window === 'undefined') return EMPTY_ANSWERS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY_ANSWERS
    const parsed = JSON.parse(raw) as Partial<WizardAnswers>
    return {
      ...EMPTY_ANSWERS,
      ...parsed,
      medical: Array.isArray(parsed.medical) ? parsed.medical : [],
    }
  } catch {
    return EMPTY_ANSWERS
  }
}

export function saveAnswers(answers: WizardAnswers) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(answers))
  } catch {
    // Storage unavailable — wizard still works for this page view
  }
}

export function loadStep(): number {
  if (typeof window === 'undefined') return 0
  try {
    const raw = window.localStorage.getItem(STEP_KEY)
    if (!raw) return 0
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
  } catch {
    return 0
  }
}

export function saveStep(step: number) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STEP_KEY, String(step))
  } catch {
    // Non-critical
  }
}

export function clearWizardStorage() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
    window.localStorage.removeItem(STEP_KEY)
    window.localStorage.removeItem(DONE_KEY)
  } catch {
    // Non-critical
  }
}
